import { useState, useMemo } from 'react'
import { FileText, ArrowUpDown, ArrowUp, ArrowDown, Search } from 'lucide-react'

interface TopPage {
  path: string
  views: number
  avgDuration: number
  bounceRate: number
  sharePercent: number
}

interface TopPagesTableProps {
  pages: TopPage[]
}

type SortKey = 'path' | 'sharePercent' | 'views' | 'avgDuration' | 'bounceRate'
type SortDir = 'asc' | 'desc'

function formatDuration(s: number): string {
  const minutes = Math.floor(s / 60)
  const seconds = Math.floor(s % 60)
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`
}

const COLUMNS: { key: SortKey; label: string; align: string }[] = [
  { key: 'path', label: 'Page', align: 'text-left' },
  { key: 'sharePercent', label: 'Part', align: 'text-left' },
  { key: 'views', label: 'Vues', align: 'text-right' },
  { key: 'avgDuration', label: 'Dur\u00e9e moy.', align: 'text-right' },
  { key: 'bounceRate', label: 'Rebond', align: 'text-right' },
]

export function TopPagesTable({ pages }: TopPagesTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('views')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [search, setSearch] = useState('')

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir(key === 'path' ? 'asc' : 'desc')
    }
  }

  const filtered = useMemo(() => {
    let list = pages
    if (search) {
      const needle = search.toLowerCase()
      list = list.filter(p => p.path.toLowerCase().includes(needle))
    }
    return [...list].sort((a, b) => {
      const av = a[sortKey]
      const bv = b[sortKey]
      if (typeof av === 'string' && typeof bv === 'string') {
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av)
      }
      return sortDir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number)
    })
  }, [pages, search, sortKey, sortDir])

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ArrowUpDown className="w-3 h-3 text-[#B0B4C5]" strokeWidth={1.25} />
    return sortDir === 'asc'
      ? <ArrowUp className="w-3 h-3 text-[#6366F1]" strokeWidth={1.5} />
      : <ArrowDown className="w-3 h-3 text-[#6366F1]" strokeWidth={1.5} />
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-[#1B2559]">
          Pages les plus visit&eacute;es
        </h3>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#8B8FA7]" strokeWidth={1.25} />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Filtrer par URL..."
            className="pl-8 pr-3 py-1.5 text-xs border border-[#E2E4ED] rounded-lg text-[#1B2559] placeholder:text-[#B0B4C5] w-52 focus:outline-none focus:ring-1 focus:ring-[#6366F1]"
          />
        </div>
      </div>

      <div className="bg-white border border-[#E2E4ED] rounded-xl overflow-hidden">
        {filtered.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-[#8B8FA7]">
            {search ? `Aucune page correspondant \u00e0 "${search}"` : 'Aucune donn\u00e9e disponible'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E2E4ED]">
                {COLUMNS.map(col => (
                  <th
                    key={col.key}
                    className={`${col.align} px-5 py-3 text-[#555A73] font-medium cursor-pointer select-none hover:text-[#1B2559] transition-colors`}
                    onClick={() => handleSort(col.key)}
                  >
                    <div className={`flex items-center gap-1.5 ${col.align === 'text-right' ? 'justify-end' : ''}`}>
                      {col.label}
                      <SortIcon col={col.key} />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((page) => (
                <tr
                  key={page.path}
                  className="border-b border-[#E2E4ED] last:border-0 hover:bg-[#F4F5FA] transition-colors"
                >
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2 max-w-[300px]">
                      <FileText className="w-3.5 h-3.5 text-[#555A73] shrink-0" strokeWidth={1.25} />
                      <span className="text-[#1B2559] truncate" title={page.path}>{page.path}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 bg-[#F4F5FA] rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#6366F1]/70 rounded-full"
                          style={{ width: `${Math.min(page.sharePercent, 100)}%` }}
                        />
                      </div>
                      <span className="font-mono text-[#8B8FA7] text-xs">
                        {page.sharePercent.toFixed(1)}%
                      </span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-right font-mono text-[#8B8FA7]">
                    {page.views.toLocaleString('fr-FR')}
                  </td>
                  <td className="px-5 py-3 text-right font-mono text-[#8B8FA7]">
                    {formatDuration(page.avgDuration)}
                  </td>
                  <td className="px-5 py-3 text-right font-mono text-[#8B8FA7]">
                    {(page.bounceRate * 100).toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
